import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ADIProductSaleInfo from "@NA/Components/ProductList/ADIProductSaleInfo";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIColors from "@NA/Utils/ADIColors";
import React, { useState } from "react";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css } from "styled-components";
import { isProductInventoryOnlyRole } from "@NA/Widgets/MyAccount/ADIMyAccount";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import translate from "@insite/client-framework/Translate";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    productSettings: getSettingsCollection(state).productSettings,
    isAuthenticated: state.context.session.isAuthenticated,
    roles: state.context.session.userRoles,
    catalogSettings: state.context.settings.settingsCollection.catalogSettingSettings,
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    extendedStyles?: ProductCarouselProductFullStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductFullStyles {
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    productImageStyles?: ProductImageStyles;
    brandGridItem?: GridItemProps;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    descriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    partNumberGridItem?: GridItemProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    unitOfMeasuresGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityBreakPricingGridItem?: GridItemProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    quantityOrderedGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    productStandardActionsContainer?: GridContainerProps;
    addToCartGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    addToListGridItem?: GridItemProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    priceTextSin?: InjectableCss;
    tags?: InjectableCss;
    saletagparent?: InjectableCss;
}

export const productCarouselProductFullStyles: ProductCarouselProductFullStyles = {
    container: {
        gap: 8,
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    imageGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                justify-content: center;
                max-width: 100px;
                margin: 0 auto;
            `,
        },
    },
    brandGridItem: {
        width: 12,
    },
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    descriptionGridItem: {
        width: 12,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
            typographyProps: {
                size: 18,
                weight: 600,
                lineHeight: 1.22,
                color: ADIColors.blue1,
                css: css`
                    display: -webkit-box;
                    -webkit-line-clamp: 2 !important;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                `,
            },
        },
    },
    partNumberGridItem: {
        width: 12,
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    availabilityGridItem: {
        width: 12,
    },
    priceGridItem: {
        width: 12,
    },
    productPriceStyles: {
        price: {
            priceText: {
                size: 20,
                weight: 900,
                color: `${ADIColors.black}`,
            },
        },
    },
    unitOfMeasuresGridItem: {
        width: 12,
    },
    priceTextSin: {
        css: css`
            font-size: 18px;
            font-weight: 900;
            padding: 16px 8px 8px 16px !important;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                flex: 1;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                flex: 1;
            `,
        },
    },
    quantityBreakPricingGridItem: {
        width: 12,
    },
    quantityOrderedGridItem: {
        width: 12,
    },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                flex: 1;
            `,
        },
        labelPosition: "left",
        cssOverrides: {
            formInputWrapper: css`
                flex: 1;
            `,
        },
    },
    productStandardActionsContainer: {
        gap: 6,
        css: css`
            padding-top: 12px;
        `,
    },
    addToCartGridItem: {
        width: 12,
        css: css`
            padding-bottom: 33px;
            > ${FormFieldStyle} {
                width: 35%;
                margin-right: 17px;
                > ${TypographyStyle} {
                    display: none;
                }
            }
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    addToListGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    tags: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            padding: 4px 15px !important;
            background-color: #2380a3;
            flex-grow: 0;
            font-size: 12px;
            font-weight: 800;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            text-align: center;
            color: #fff;
            border-radius: 0;
            border-top-left-radius: 5px;
            border-bottom-right-radius: 5px;
            min-height: 24px;
            width: auto;
            line-height: 1;
            white-space: normal;
            height: auto;
        `,
    },
    saletagparent: {
        css: css`
            position: absolute;
        `,
    },
};

const ProductCarouselProductFull = ({
    carouselId,
    product,
    extendedStyles,
    productInfo,
    isAuthenticated,
    productSettings,
    updateProductInfo,
    actionsTemplate,
    roles,
    catalogSettings,
}: Props) => {
    const showAuthorizationRequired =
        productInfo?.pricing &&
        productInfo?.pricing?.additionalResults &&
        productInfo?.pricing?.additionalResults.isVisible &&
        productInfo?.pricing?.additionalResults.isVisible === "N"
            ? false
            : true;
        ;        

    const [styles] = useState(() => mergeToNew(productCarouselProductFullStyles, extendedStyles));
    const [hideQuantity, setHideQuantity] = useState(false);
    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    const isSale =
        product.properties["saletype"] !== "ED" &&
        product.properties["saletype"] !== "CD" &&
        product.properties["saletype"] !== "DD" &&
        product.properties["saletype"] !== "WS" &&
        product.properties["saletype"] !== "OO" &&
        product.properties["saletype"] !== "SD" &&
        product.properties["saletype"] !== "LT" &&
        product.properties["saletype"] !== "FS" &&
        product.properties["saletype"] !== "BF" &&
        product.properties["saletype"] !== "EV" &&
        product.properties["saletype"] !== "SM" &&
        product.properties["saletype"] !== "AC" &&
        product.properties["classificatioN_CODE"] !== "X" &&
        product.properties["onsalenow"] === "1";
    const pricing = productInfo?.pricing || product.pricing;

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const formatPriceToDisplayforPIOComparison = (catalogSettings: any) => {
        const priceSettingForPIORole = catalogSettings.priceForPIORole;

        if (priceSettingForPIORole) {
            return (
                <>
                    <GridContainer {...styles.priceTextSin}>{priceSettingForPIORole}</GridContainer>
                </>
            );
        }
    };

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <GridContainer
                    id={`productContainer_${product.id}`}
                    {...styles.container}
                    data-test-selector="productContainer"
                >
                    <GridItem {...styles.descriptionGridItem}>
                        {isSale ? (
                            <StyledWrapper {...styles.saletagparent}>
                                <Typography className="saletag" {...styles.tags}>
                                    {translate("Sale")}
                                </Typography>
                            </StyledWrapper>
                        ) : (
                            <ADIProductSaleInfo product={product} isAuthenticated={false} pricing={pricing} />
                        )}
                    </GridItem>
                    <GridItem {...styles.imageGridItem}>
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </GridItem>
                    <GridItem {...styles.descriptionGridItem}>
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    </GridItem>

                    <GridItem {...styles.priceGridItem}>
                        {showAuthorizationRequired &&
                            (!isProductInventoryOnlyRole(roles) ? (
                                <ProductPrice
                                    showLabel={false}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    product={productContext}
                                    extendedStyles={styles.productPriceStyles}
                                />
                            ) : (
                                formatPriceToDisplayforPIOComparison(catalogSettings)
                            ))}
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.productStandardActionsContainer}>
                    <GridItem {...styles.addToCartGridItem}>
                        {!isPendingVerification &&
                            showAuthorizationRequired &&
                            productInfo?.pricing !== undefined &&
                            !hideQuantity && (
                                <ProductQuantityOrdered extendedStyles={styles.productQuantityOrderedStyles} />
                            )}
                        {!isPendingVerification && (
                            <ProductAddToCartButton
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                                onAddToCartClick={() => setHideQuantity(true)}
                            />
                        )}
                    </GridItem>
                </GridContainer>
                {actionsTemplate ? actionsTemplate(product) : []}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductFull);
